import { motion, AnimatePresence } from 'framer-motion';
import { Share2, Twitter, Facebook, Link as LinkIcon, Check, MessageCircle, Send, Linkedin } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Iter 213 — fire-and-forget share tracking so the admin Share Analytics
// dashboard finally fills up. Any failure is silent so a flaky network
// never blocks the actual share intent.
const trackShare = (platform, type, contentId, shareUrl) => {
  try {
    axios.post(
      `${API}/api/shareable-content/record-share`,
      {
        platform,
        content_type: type || 'content',
        content_id: contentId || undefined,
        url: shareUrl || undefined,
      },
      { withCredentials: true, timeout: 3000 },
    ).catch(() => {});
  } catch { /* noop */ }
};

export const resolveShareUrl = (url) => {
  const browserUrl = typeof window !== 'undefined' ? window.location.href : 'https://rastyle.co.ke/';
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://rastyle.co.ke';
  try {
    return new URL(url || browserUrl, baseUrl).toString();
  } catch {
    return browserUrl;
  }
};

const ShareButton = ({
  title,
  url,
  description = '',
  type = 'content',
  contentId = '',
  compact = false,
  label = 'Share',
  className = '',
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef(null);

  const shareUrl = resolveShareUrl(url);
  const shareText = description || `Check out "${title}" on Rastyle Republik! #FeelTheVibe #MyStyleIsRastyle`;

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
      trackShare('copy', type, contentId, shareUrl);
    } catch (error) {
      toast.error('Failed to copy link');
    }
    setShowMenu(false);
  };

  const shareToTwitter = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(twitterUrl, '_blank', 'width=550,height=420');
    trackShare('twitter', type, contentId, shareUrl);
    setShowMenu(false);
  };

  const shareToFacebook = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(facebookUrl, '_blank', 'width=550,height=420');
    trackShare('facebook', type, contentId, shareUrl);
    setShowMenu(false);
  };

  const shareToWhatsApp = () => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
    window.open(whatsappUrl, '_blank');
    trackShare('whatsapp', type, contentId, shareUrl);
    setShowMenu(false);
  };

  const shareToTelegram = () => {
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
    window.open(telegramUrl, '_blank');
    trackShare('telegram', type, contentId, shareUrl);
    setShowMenu(false);
  };

  const shareToLinkedIn = () => {
    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(linkedinUrl, '_blank', 'width=550,height=420');
    trackShare('linkedin', type, contentId, shareUrl);
    setShowMenu(false);
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: shareText,
          url: shareUrl
        });
        trackShare('native', type, contentId, shareUrl);
      } catch (error) {
        if (error.name !== 'AbortError') {
          setShowMenu(true);
        }
      }
    } else {
      setShowMenu(true);
    }
  };

  const shareOptions = [
    { name: 'Twitter / X', icon: Twitter, action: shareToTwitter, color: 'hover:text-blue-400' },
    { name: 'Facebook', icon: Facebook, action: shareToFacebook, color: 'hover:text-blue-500' },
    { name: 'WhatsApp', icon: MessageCircle, action: shareToWhatsApp, color: 'hover:text-green-500' },
    { name: 'Telegram', icon: Send, action: shareToTelegram, color: 'hover:text-blue-400' },
    { name: 'LinkedIn', icon: Linkedin, action: shareToLinkedIn, color: 'hover:text-blue-600' },
  ];

  return (
    <div className={`relative ${className}`} ref={menuRef} data-testid="share-button-container">
      <button
        onClick={shareNative}
        data-testid="share-button"
        className={`flex items-center gap-2 transition-colors duration-300 ${
          compact 
            ? 'p-2 hover:bg-white/10 rounded-full' 
            : 'px-3 py-2 hover:bg-white/10 rounded-sm'
        }`}
        title={`${label} ${title || 'Rastyle Republik'}`}
        aria-label={`${label} ${title || 'Rastyle Republik'}`}
      >
        <Share2 className={compact ? 'w-4 h-4' : 'w-5 h-5'} />
        {!compact && <span className="text-sm">{label}</span>}
      </button>

      <AnimatePresence>
        {showMenu && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-52 glass-card rounded-sm p-2 z-50 shadow-xl"
            data-testid="share-menu"
          >
            <p className="px-3 py-2 text-xs text-muted-foreground border-b border-white/10 mb-2">
              Share to...
            </p>
            
            {shareOptions.map(option => {
              const Icon = option.icon;
              return (
                <button
                  key={option.name}
                  onClick={option.action}
                  data-testid={`share-${option.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/10 rounded-sm transition-colors duration-200 ${option.color}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm">{option.name}</span>
                </button>
              );
            })}
            
            <div className="border-t border-white/10 mt-2 pt-2">
              <button
                onClick={copyToClipboard}
                data-testid="share-copy-link"
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/10 rounded-sm transition-colors duration-200"
              >
                {copied ? (
                  <Check className="w-4 h-4 text-accent" />
                ) : (
                  <LinkIcon className="w-4 h-4" />
                )}
                <span className="text-sm">{copied ? 'Copied!' : 'Copy Link'}</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Inline share bar for article/event pages
export const ShareBar = ({ title, url, type = 'content', contentId = '', className = '' }) => {
  const shareUrl = resolveShareUrl(url);
  const shareText = `Check out "${title}" on Rastyle Republik!`;

  const shareActions = [
    { 
      icon: Twitter, 
      label: 'Twitter', 
      color: 'bg-blue-500 hover:bg-blue-600',
      action: () => {
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
        trackShare('twitter', type, contentId, shareUrl);
      }
    },
    { 
      icon: Facebook, 
      label: 'Facebook', 
      color: 'bg-blue-600 hover:bg-blue-700',
      action: () => {
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
        trackShare('facebook', type, contentId, shareUrl);
      }
    },
    { 
      icon: MessageCircle, 
      label: 'WhatsApp', 
      color: 'bg-green-500 hover:bg-green-600',
      action: () => {
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`, '_blank');
        trackShare('whatsapp', type, contentId, shareUrl);
      }
    },
    { 
      icon: Send, 
      label: 'Telegram', 
      color: 'bg-blue-400 hover:bg-blue-500',
      action: () => {
        window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`, '_blank');
        trackShare('telegram', type, contentId, shareUrl);
      }
    },
  ];

  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="share-bar">
      <span className="text-sm text-muted-foreground mr-2">Share:</span>
      {shareActions.map(action => {
        const Icon = action.icon;
        return (
          <button
            key={action.label}
            onClick={action.action}
            data-testid={`share-bar-${action.label.toLowerCase()}`}
            className={`p-2 rounded-full ${action.color} text-white transition-colors`}
            title={`Share on ${action.label}`}
          >
            <Icon className="w-4 h-4" />
          </button>
        );
      })}
    </div>
  );
};

export default ShareButton;
